/**
 * Maker Kids Cohort Modal – Multi-step form navigation
 *
 * Steps:
 *   1  Program snapshot  (no inputs; introduction)
 *   2  Parent / guardian (name, email, phone, timezone)
 *   3  Child details     (name, age range, interests)
 *   4  Availability      (preferred time, notes; submit button)
 *
 * Form submission is handled by assets/modal-form-handler.js (Formspark).
 * This file only controls step visibility, validation, and navigation UI.
 */
(function () {
  'use strict';

  var MODAL_ID    = 'kraft-maker-kids-cohort-modal';
  var TOTAL_STEPS = 4;

  var DEFAULT_SUBTITLE = 'Virtual · 8 weeks · Live sessions + at-home making';

  var STEP_CONTENT = [
    {
      title:    'Apply for the Next Cohort',
      subtitle: DEFAULT_SUBTITLE
    },
    {
      title:    'About you',
      subtitle: 'A parent or guardian fills this in — takes about 30 seconds.'
    },
    {
      title:    'About your child',
      subtitle: "We'll use this to match their kit and session style."
    },
    {
      title:    'Last step',
      subtitle: "Tell us your preferred session time and we'll do the rest."
    }
  ];

  var NEXT_LABELS = [
    'Tell us about you',
    'About your child',
    'Final step',
    ''
  ];

  var state = { currentStep: 1, activeVariant: null };

  /* ── DOM helpers ── */

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function goToStep(step) {
    var modal = getModal();
    if (!modal) return;

    state.currentStep = step;

    // Show / hide step panels
    modal.querySelectorAll('.kraft-cohort-step').forEach(function (el) {
      el.classList.toggle('active', parseInt(el.getAttribute('data-cohort-step'), 10) === step);
    });

    // Update step heading and subtitle
    var titleEl    = modal.querySelector('[data-cohort-step-title]');
    var subtitleEl = modal.querySelector('[data-cohort-step-subtitle]');
    if (titleEl)    titleEl.textContent    = STEP_CONTENT[step - 1].title;
    if (subtitleEl) subtitleEl.textContent = STEP_CONTENT[step - 1].subtitle;

    // Update step counter label
    var countEl = modal.querySelector('[data-cohort-step-count]');
    if (countEl) countEl.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;

    // Update progress bar
    var progressFill = modal.querySelector('[data-cohort-progress]');
    if (progressFill) {
      progressFill.style.width = ((step / TOTAL_STEPS) * 100) + '%';
    }

    // Back button: hidden on step 1
    var backBtn = modal.querySelector('[data-cohort-back]');
    if (backBtn) {
      backBtn.classList.toggle('visible', step > 1);
    }

    // Next button wrap: hidden on step 4 (submit button is inside step 4)
    var nextWrap = modal.querySelector('.kraft-cohort-next-wrap');
    if (nextWrap) {
      nextWrap.classList.toggle('is-hidden', step === TOTAL_STEPS);
    }

    // Update Next button label
    var nextLabel = modal.querySelector('[data-cohort-next-label]');
    if (nextLabel && NEXT_LABELS[step - 1]) {
      nextLabel.textContent = NEXT_LABELS[step - 1];
    }

    // Footer: hide entirely on step 4 (step 4 has its own submit inside the step)
    var footer = modal.querySelector('.kraft-cohort-footer');
    if (footer) {
      footer.style.display = step === TOTAL_STEPS ? 'none' : '';
    }

    // Clear any inline validation errors from the new step
    clearErrors(modal, step);
  }

  /* ── Validation ── */

  function validateStep(step) {
    var modal = getModal();
    if (!modal) return true;

    var stepEl = modal.querySelector('[data-cohort-step="' + step + '"]');
    if (!stepEl) return true;

    var requiredFields = stepEl.querySelectorAll('[data-cohort-required]');
    var valid = true;

    requiredFields.forEach(function (field) {
      removeFieldError(field);
      var value = field.value.trim();
      if (!value) {
        showFieldError(field, getFieldErrorMessage(field));
        valid = false;
      } else if (field.type === 'email' && !isValidEmail(value)) {
        showFieldError(field, 'Please enter a valid email address.');
        valid = false;
      }
    });

    return valid;
  }

  function getFieldErrorMessage(field) {
    var placeholder = field.getAttribute('placeholder') || field.tagName.toLowerCase();
    if (field.type === 'email') return 'Email is required.';
    if (field.tagName.toLowerCase() === 'select') return 'Please make a selection.';
    return placeholder.replace(' (optional)', '') + ' is required.';
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function showFieldError(field, message) {
    field.classList.add('kraft-cohort-field-error');
    var errorEl = document.createElement('p');
    errorEl.className = 'kraft-cohort-error-msg';
    errorEl.textContent = message;
    field.parentNode.insertBefore(errorEl, field.nextSibling);
  }

  function removeFieldError(field) {
    field.classList.remove('kraft-cohort-field-error');
    var next = field.nextSibling;
    if (next && next.classList && next.classList.contains('kraft-cohort-error-msg')) {
      next.parentNode.removeChild(next);
    }
  }

  function clearErrors(modal, step) {
    var stepEl = modal.querySelector('[data-cohort-step="' + step + '"]');
    if (!stepEl) return;
    stepEl.querySelectorAll('.kraft-cohort-field-error').forEach(function (el) {
      el.classList.remove('kraft-cohort-field-error');
    });
    stepEl.querySelectorAll('.kraft-cohort-error-msg').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
  }

  /* ── Reset on open ── */

  function resetModal() {
    var modal = getModal();
    if (!modal) return;

    var form = modal.querySelector('form');
    if (form) form.reset();

    // Clear all errors
    modal.querySelectorAll('.kraft-cohort-field-error').forEach(function (el) {
      el.classList.remove('kraft-cohort-field-error');
    });
    modal.querySelectorAll('.kraft-cohort-error-msg').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });

    // Hide any lingering success/error messages
    var success = modal.querySelector('.kraft-modal-success');
    if (success) success.style.display = 'none';
    if (form) form.style.display = '';

    // Re-apply program variant (capture click sets it before modal:opened fires)
    if (state.activeVariant) {
      applyVariantToDOM(state.activeVariant);
    }

    goToStep(1);
  }

  /* ── Initialise ── */

  function initModal() {
    var modal = getModal();
    if (!modal) return;

    // Next button
    var nextBtn = modal.querySelector('[data-cohort-next]');
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!validateStep(state.currentStep)) return;
        if (state.currentStep < TOTAL_STEPS) {
          goToStep(state.currentStep + 1);
        }
      });
    }

    // Back button
    var backBtn = modal.querySelector('[data-cohort-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (state.currentStep > 1) {
          goToStep(state.currentStep - 1);
        }
      });
    }

    // Clear error on input / change
    modal.querySelectorAll('[data-cohort-required]').forEach(function (field) {
      field.addEventListener('input', function () { removeFieldError(field); });
      field.addEventListener('change', function () { removeFieldError(field); });
    });
  }

  /* ── Per-section modal image ── */

  function getLeftArea() {
    var m = getModal();
    return m ? m.querySelector('#maker-kids-cohort-modal-left-area') : null;
  }

  function applyModalImage(imageUrl, imageText) {
    var leftArea = getLeftArea();
    if (!leftArea) return;

    var img = leftArea.querySelector('.kraft-cohort-modal-img');
    var textEl = leftArea.querySelector('#maker-kids-cohort-modal-left-text');

    if (img) {
      img.src = imageUrl || '';
      img.removeAttribute('srcset');
      img.style.display = imageUrl ? '' : 'none';
    }
    if (textEl) {
      textEl.textContent = imageText || '';
      textEl.style.display = imageText ? '' : 'none';
    }

    leftArea.classList.toggle('kraft-modal-left-area--empty', !imageUrl);
  }

  function resetToDefaultImage() {
    var modal = getModal();
    if (!modal) return;
    var defaultImage = modal.getAttribute('data-cohort-default-image') || '';
    var defaultText = modal.getAttribute('data-cohort-default-image-text') || '';
    applyModalImage(defaultImage, defaultText);
  }

  /* ── Program-specific content override ── */

  var SUMMER_CAMP_KEYWORD = 'Summer Camp';

  var PROGRAM_VARIANTS = {
    default: {
      subtitle: 'Virtual \u00b7 8 weeks \u00b7 Live sessions + at-home making',
      program: 'Maker Kids Studio \u2013 Virtual 8-Week Cohort',
      marker: '8 wks',
      strong: 'One cohort at a time',
      desc: 'Small group. Intentional pace. Real projects from day one.',
      commitNote: 'Sessions run <strong>once a week</strong> for 8 weeks \u2014 approximately 60\u201390 minutes each. A physical making kit will ship to your door before the cohort starts. No payment is required today.'
    },
    summerCamp: {
      subtitle: 'Virtual \u00b7 12 weeks \u00b7 Immersive summer design camp',
      program: 'Future Designers \u2014 12-Week Summer Camp',
      marker: '12 wks',
      strong: 'Two sessions per week',
      desc: 'An immersive summer experience with real projects and a final presentation.',
      commitNote: 'Sessions run <strong>twice a week</strong> for 12 weeks \u2014 approximately 60\u201390 minutes each. This form is an application only \u2014 no payment is collected here.'
    }
  };

  function resolveVariant(trigger) {
    if (!trigger) return PROGRAM_VARIANTS.default;
    var workshop = trigger.getAttribute('data-prefill-workshop') || '';
    return workshop.indexOf(SUMMER_CAMP_KEYWORD) !== -1 ? PROGRAM_VARIANTS.summerCamp : PROGRAM_VARIANTS.default;
  }

  function applyVariantToDOM(variant) {
    var modal = getModal();
    if (!modal || !variant) return;

    STEP_CONTENT[0].subtitle = variant.subtitle;

    var programInput = modal.querySelector('[data-cohort-program]');
    if (programInput) programInput.value = variant.program;

    var snapshotEl = modal.querySelector('[data-cohort-snapshot-duration]');
    if (snapshotEl) {
      var markerEl = snapshotEl.querySelector('.kraft-cohort-snapshot-marker');
      var strongEl = snapshotEl.querySelector('.kraft-cohort-snapshot-text strong');
      var descEl = snapshotEl.querySelector('.kraft-cohort-snapshot-text span');
      if (markerEl) markerEl.textContent = variant.marker;
      if (strongEl) strongEl.textContent = variant.strong;
      if (descEl) descEl.textContent = variant.desc;
    }

    var commitEl = modal.querySelector('[data-cohort-commit-note]');
    if (commitEl) {
      commitEl.innerHTML = '<p>' + variant.commitNote + '</p>';
    }
  }

  /*
   * Capture-phase click: runs BEFORE the generic modal opener adds the active class.
   * Resolves the trigger button directly from the click target, reads data-modal-image,
   * and applies it to the modal left area immediately. No stored state, no timing issues.
   */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-modal-trigger="' + MODAL_ID + '"]');
    if (!trigger) return;

    var modal = getModal();
    if (!modal) return;
    var defaultImage = modal.getAttribute('data-cohort-default-image') || '';
    var defaultText = modal.getAttribute('data-cohort-default-image-text') || '';

    var triggerImage = trigger.getAttribute('data-modal-image');
    var triggerText = trigger.getAttribute('data-modal-image-text');

    var imageUrl = triggerImage || defaultImage;
    var imageText = triggerImage ? (triggerText || '') : defaultText;

    applyModalImage(imageUrl, imageText);
    state.activeVariant = resolveVariant(trigger);
    applyVariantToDOM(state.activeVariant);
  }, true);

  /* Reset image to theme default when modal closes */
  (function setupCloseObserver() {
    var modal = getModal();
    if (!modal) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'class' && !modal.classList.contains('active')) {
          resetToDefaultImage();
        }
      });
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ── Listen for modal open ── */

  window.addEventListener('modal:opened', function (e) {
    if (e.detail && e.detail.modalId === MODAL_ID) {
      resetModal();
    }
  });

  /* ── Boot ── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModal);
  } else {
    initModal();
  }

})();
